import React, { useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { FileSpreadsheet, Loader2, Upload, FileDown } from "lucide-react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const STATUS = {
  neu: { label: "Neu", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  vorhanden: { label: "Bereits vorhanden", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  fehler: { label: "Fehler", cls: "bg-red-50 text-red-700 border-red-200" },
};

export default function ImportInspectionsDialog({ open, onOpenChange, onImported }) {
  const fileRef = useRef(null);
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [rows, setRows] = useState(null);
  const [summary, setSummary] = useState(null);
  const [createCompanies, setCreateCompanies] = useState(true);
  const [projects, setProjects] = useState([]);
  const [assets, setAssets] = useState([]);
  const [projectId, setProjectId] = useState("none");
  const [assetId, setAssetId] = useState("none");
  const [file, setFile] = useState(null);

  React.useEffect(() => {
    if (!open) return;
    api.get("/projects").then((r) => setProjects(r.data || [])).catch(() => {});
    api.get("/inspections/assets").then((r) => setAssets(r.data || [])).catch(() => {});
  }, [open]);

  const reset = () => { setRows(null); setSummary(null); setFile(null); if (fileRef.current) fileRef.current.value = ""; };
  const close = (v) => { if (!v) reset(); onOpenChange(v); };
  const downloadTemplate = async () => {
    try {
      const res = await api.get("/inspections/import/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "Pruefungen_Vorlage.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Vorlage konnte nicht geladen werden"); }
  };

  const parse = async (f, pid) => {
    if (!f) return;
    setParsing(true); setRows(null); setSummary(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      if (pid && pid !== "none") fd.append("project_id", pid);
      const res = await api.post("/inspections/import/preview", fd);
      setRows(res.data.rows || []);
      setSummary(res.data.summary || null);
      if (!(res.data.rows || []).length) toast.info("Keine verwertbaren Zeilen gefunden.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Datei konnte nicht gelesen werden");
    } finally { setParsing(false); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    await parse(f, projectId);
  };

  // Re-check duplicates per selected project ("Haus") when the project changes.
  React.useEffect(() => {
    if (file) parse(file, projectId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const startImport = async () => {
    const toCreate = (rows || []).filter((r) => r.status === "neu");
    if (!toCreate.length) return toast.info("Keine neuen Prüfungen zum Importieren.");
    setImporting(true);
    try {
      const res = await api.post("/inspections/import/commit", { rows: toCreate, create_companies: createCompanies, project_id: projectId === "none" ? null : projectId, asset_id: assetId === "none" ? null : assetId });
      const s = res.data;
      toast.success(`Import abgeschlossen: ${s.created} neu${s.new_companies ? `, ${s.new_companies} Firmen angelegt` : ""}`);
      onImported?.();
      close(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Import fehlgeschlagen");
    } finally { setImporting(false); }
  };

  const missingCompanies = (rows || []).some((r) => r.status === "neu" && r.company && !r.company_exists);

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-3xl" data-testid="import-inspections-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-primary" />Prüfungen aus Excel importieren</DialogTitle>
          <DialogDescription>
            .xlsx mit Spalten: 1) Nummer/ID · 2) Prüfungsbezeichnung · 3) Intervall · 4) Firma/Wartungsfirma.
          </DialogDescription>
        </DialogHeader>

        <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={onFile} data-testid="import-file-input" />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-slate-500">Projekt (optional)</label>
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger data-testid="import-project-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Kein Projekt</SelectItem>
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-500">Anlage (optional)</label>
            <Select value={assetId} onValueChange={setAssetId}>
              <SelectTrigger data-testid="import-asset-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Keine Anlage</SelectItem>
                {assets.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {!rows && (
          <div className="py-8 flex flex-col items-center gap-3">
            <Button onClick={() => fileRef.current?.click()} disabled={parsing} data-testid="import-choose-file-btn">
              {parsing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}Excel-Datei auswählen
            </Button>
            <button onClick={downloadTemplate} className="inline-flex items-center text-xs text-primary hover:underline" data-testid="import-template-btn">
              <FileDown className="h-3.5 w-3.5 mr-1" />Leere Vorlage herunterladen
            </button>
            <p className="text-xs text-slate-400">Nur .xlsx-Dateien</p>
          </div>
        )}

        {rows && (
          <>
            {summary && (
              <div className="flex flex-wrap gap-2 text-sm">
                <Badge variant="outline">{summary.total} gelesen</Badge>
                <Badge variant="outline" className={STATUS.neu.cls}>{summary.neu} neu</Badge>
                <Badge variant="outline" className={STATUS.vorhanden.cls}>{summary.vorhanden} vorhanden</Badge>
                <Badge variant="outline" className={STATUS.fehler.cls}>{summary.fehler} fehlerhaft</Badge>
              </div>
            )}
            <div className="max-h-[45vh] overflow-y-auto border border-slate-200 rounded-lg" data-testid="import-preview-table">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 sticky top-0">
                  <tr>
                    <th className="text-left p-2 font-semibold">Prüfungsbezeichnung</th>
                    <th className="text-left p-2 font-semibold">Intervall</th>
                    <th className="text-left p-2 font-semibold">Firma</th>
                    <th className="text-left p-2 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className="border-t border-slate-100" data-testid={`import-row-${i}`}>
                      <td className="p-2 text-slate-900">{r.title || <span className="text-slate-400">—</span>}</td>
                      <td className="p-2 text-slate-700">{r.interval_label || r.interval_raw || "—"}</td>
                      <td className="p-2 text-slate-700">
                        {r.company || "—"}
                        {r.status === "neu" && r.company && !r.company_exists && <span className="ml-1 text-[10px] text-amber-600">(neu)</span>}
                      </td>
                      <td className="p-2">
                        <Badge variant="outline" className={STATUS[r.status]?.cls}>{STATUS[r.status]?.label || r.status}</Badge>
                        {r.error && <div className="text-[11px] text-red-600 mt-0.5">{r.error}</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {missingCompanies && (
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <Checkbox checked={createCompanies} onCheckedChange={(v) => setCreateCompanies(!!v)} data-testid="import-create-companies" />
                Noch nicht vorhandene Firmen automatisch anlegen
              </label>
            )}
          </>
        )}

        <DialogFooter className="gap-2">
          {rows && <Button variant="outline" onClick={() => reset()} data-testid="import-choose-another-btn">Andere Datei</Button>}
          <Button variant="ghost" onClick={() => close(false)} data-testid="import-cancel-btn">Abbrechen</Button>
          {rows && (
            <Button onClick={startImport} disabled={importing || !(rows || []).some((r) => r.status === "neu")} data-testid="import-start-btn">
              {importing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Import starten
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
